import { mutations, queries } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useContext } from "react";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext } from "../../app/providers";

interface UseUnreadNotificationReturn {
  unreadNotification: (_variables: Record<string, any>) => void;
  unreading: boolean;
}

/**
 * Custom hook to mark a notification as unread.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @param {Array} [customRefetchQueries] - Optional custom refetch queries.
 */
export function useUnreadNotification(
  onCompleted: (_data: any) => void = () => {},
  onError: (_err: any) => void = () => {},
  customRefetchQueries?: any[],
): UseUnreadNotificationReturn {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [unreadNotification, { loading }] = useMutation<any>(
    mutations.unreadNotification,
    {
      onCompleted: (_data) => {
        onCompleted(_data);

        enqueueSnackbar(gettext("Notification marked as unread"), {
          variant: "success",
        });
      },
      onError: (_err) => {
        onError(_err);

        enqueueSnackbar("Unread Notification: " + _err.message, {
          variant: "error",
        });
      },
      update: (cache, _result, { variables }) => {
        if (variables?.notificationId) {
          cache.modify({
            id: cache.identify({
              __typename: "NotificationType",
              id: variables.notificationId,
            }),
            fields: {
              Read() {
                return false;
              },
            },
          });
        }
      },
      refetchQueries: customRefetchQueries ?? [queries.getNewNotifications],
    },
  );

  const unreadNotificationRow = useCallback(
    (_variables: Record<string, any>) => {
      unreadNotification({
        variables: _variables,
      }).then();
    },
    [unreadNotification],
  );

  return {
    unreadNotification: unreadNotificationRow,
    unreading: loading,
  };
}
